import {AppUrls} from 'shared/config/model/urls';

import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {SubmitHandler, useForm} from 'react-hook-form';
import {Link, useNavigate, useSearchParams} from 'react-router-dom';
import cn from 'classnames';
import {t} from '@lingui/core/macro';

import EmailInput from 'shared/ui/Input/InputEmail';
import Button from 'shared/ui/Button/Button/Button';
import InputPassword from 'shared/ui/Input/InputPassword';
import SpinnerWithOverlay from 'shared/ui/SpinnerWithOverlay/SpinnerWithOverlay';
import FormGroupWithError from 'shared/ui/FormGroup/FormGroupWithError';
import Key from 'shared/ui/icons/General/Key';
import {
    ILoginForm,
    authLoadingSelector,
    isAuthenticatedSelector,
    isUserLoadingSelector,
    loginUser,
} from 'entities/user';
import {useOAuthPopup} from 'features/auth/model/useOAuthPopup';
import {TypedDispatch} from 'shared/model/types';
import {redirectToReturnUrl} from 'shared/api/redirects';
import {isTranslate} from 'shared/lib/lang';
import {reachGoal, YM_GOALS} from 'shared/lib/analytics'

import {PALETTE} from 'shared/config/model/palette';

import {brandColor} from 'shared/lib/colors';

import RewardBlock from './RewardBlock/RewardBlock';
import AuthWrapper from './AuthWrapper';

import OAuthButtons from './OAuthButtons/OAuthButtons';

import styles from './Auth.module.scss';

const Login: React.FC = () => {
    const dispatch = useDispatch<TypedDispatch>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const isAuthenticated = useSelector(isAuthenticatedSelector);
    const isLoading = useSelector(authLoadingSelector);
    const isUserLoading = useSelector(isUserLoadingSelector);
    const { initOAuth, isLoading: oauthLoading, error: oauthError } = useOAuthPopup();

    const {register, handleSubmit, formState, control} = useForm<ILoginForm>({mode: 'onChange'});
    const {errors} = formState;

    useEffect(() => {
        if (!isUserLoading && isAuthenticated) {
            redirectToReturnUrl(navigate, searchParams) || navigate(AppUrls.HTTP_MONITORS);
        }
    }, [isAuthenticated, isUserLoading, navigate, searchParams]);

    if (isUserLoading) {
        return (
            <AuthWrapper>
                <SpinnerWithOverlay className={styles.overlay}/>
            </AuthWrapper>
        );
    }
    
    const onSubmit: SubmitHandler<ILoginForm> = (formData) => {
        dispatch(loginUser(formData))
            .then((result) => {
                if (loginUser.fulfilled.match(result)) {
                    reachGoal(YM_GOALS.SUBMIT_FORM_LOGIN);
                }
            });
    };

    return (
        <AuthWrapper>
            <div className={styles.title_container}>
                <h2 className={styles.title}>
                    {t`Войдите в свой аккаунт`}
                </h2>
                {!isTranslate() && <RewardBlock/>}
            </div>
            {
                !isTranslate() &&
                    <OAuthButtons onSelect={initOAuth} isLoading={oauthLoading}/>
            }
            {oauthError && <div className={styles.oauthError}>{oauthError}</div>}
            {
                !isTranslate() && <div className={styles.emailTitleLogin}>Авторизация по почте</div>
            }
            <form
                onSubmit={handleSubmit(onSubmit)}
                className="register-form"
            >
                <FormGroupWithError error={errors?.email?.message}>
                    <EmailInput
                        register={register}
                        control={control}
                        required
                        placeholderToLabel
                    />
                </FormGroupWithError>


                <FormGroupWithError error={errors?.password?.message}>
                    <InputPassword
                        register={register}
                        control={control}
                        placeholderToLabel
                    />

                </FormGroupWithError>

                <div className="form-group">
                    <Button
                        type="submit"
                        loading={isLoading}
                        block
                    >
                        {t`Войти`}
                    </Button>

                </div>

                <Link to={AppUrls.PASSWORD_RESET} className={styles.forgot_link}>
                    <Key color={brandColor(PALETTE.accent, PALETTE.accentEn)}/>
                    {t`Забыли пароль?`}
                </Link>
            </form>

            <p className={cn(styles.new_account_wrapper, 'text-muted')}>
                <span>{t`Еще нет аккаунта?`}</span>
                <Link to={AppUrls.REGISTRATION} className={styles.register_link}>
                    {t`Зарегистрироваться`}
                </Link>
            </p>
        </AuthWrapper>
    );
};

export default Login;